import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { fetchClientesPorAdministrativo, fetchClientesPorInspector } from '../admin/firestoreService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { getPdfDesignById, savePdfDesign, DEFAULT_HEADER_FIELDS } from './formHelpers/pdfDesignHelper';
import Sidebar from '../sidebar/Sidebar';
import Navbar from '../navbar/Navbar';
import { useSidebarState } from '../sidebar/SidebarStateContext';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import '../../pages/list/list.scss';

const PdfDesignEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, currentService, adminData } = useContext(AuthContext);
  const { collapsed } = useSidebarState();
  const [servicioId, setServicioId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [formId, setFormId] = useState('');
  const [forms, setForms] = useState([]);
  const [selectedFormInputs, setSelectedFormInputs] = useState([]);
  const [sections, setSections] = useState([{ title: '', fieldLabels: '' }]);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoLeftUrl, setLogoLeftUrl] = useState('');
  const [logoRightUrl, setLogoRightUrl] = useState('');
  const [companies, setCompanies] = useState([]);
  const [clientsWithLogos, setClientsWithLogos] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(null); // 'left' | 'right'
  const [primaryColor, setPrimaryColor] = useState('#FFBF00');
  const [accentColor, setAccentColor] = useState('#FFBF00');
  const [headerFields, setHeaderFields] = useState([...DEFAULT_HEADER_FIELDS]);
  const [draggedHeaderIndex, setDraggedHeaderIndex] = useState(null);
  const [error, setError] = useState('');

  const containerClass = collapsed ? 'listContainer collapsed' : 'listContainer';

  useEffect(() => {
    const fetchService = async () => {
      if (!currentUser?.email) return;
      const serviciosRef = collection(db, 'servicios');
      const q = query(serviciosRef, where('admins', 'array-contains', currentUser.email));
      const snap = await getDocs(q);
      if (!snap.empty) setServicioId(snap.docs[0].id);
    };
    fetchService();
  }, [currentUser]);

  useEffect(() => {
    if (!servicioId) return;
    const formsRef = collection(db, 'servicios', servicioId, 'forms');
    getDocs(formsRef).then((snap) => {
      setForms(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }).catch(() => setForms([]));
  }, [servicioId]);

  useEffect(() => {
    if (!servicioId) return;
    const empresasRef = collection(db, 'servicios', servicioId, 'empresas');
    getDocs(empresasRef).then((snap) => {
      setCompanies(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((e) => e.logoUrl));
    }).catch(() => setCompanies([]));
  }, [servicioId]);

  useEffect(() => {
    const svcId = currentService?.id || servicioId;
    if (!svcId || !adminData?.id) {
      setClientsWithLogos([]);
      return;
    }
    const load = async () => {
      try {
        let clientsData = [];
        const service = currentService || { id: svcId };
        if (adminData.tipoUsuario?.includes('administrativo')) {
          clientsData = await fetchClientesPorAdministrativo(adminData.id, service);
        } else if (adminData.tipoUsuario?.includes('inspector')) {
          clientsData = await fetchClientesPorInspector(adminData.id, service);
        } else if (adminData.tipoUsuario?.includes('administrador')) {
          const clientesRef = collection(db, 'servicios', svcId, 'clientes');
          const snap = await getDocs(clientesRef);
          clientsData = snap.docs.filter((d) => !d.data().borrado).map((d) => ({ id: d.id, ...d.data() }));
        } else {
          setClientsWithLogos([]);
          return;
        }
        const configsRef = collection(db, 'servicios', svcId, 'clientesConfig');
        const withLogos = [];
        for (const client of clientsData) {
          const configSnap = await getDoc(doc(configsRef, client.id));
          const logoUrl = configSnap.exists() && configSnap.data().theme?.logo?.url ? configSnap.data().theme.logo.url : null;
          if (logoUrl) {
            withLogos.push({ id: client.id, nombre: client.nombre || client.id, logoUrl });
          }
        }
        setClientsWithLogos(withLogos);
      } catch (err) {
        console.error('Error al cargar clientes con logo:', err);
        setClientsWithLogos([]);
      }
    };
    load();
  }, [servicioId, currentService?.id, adminData?.id, adminData?.tipoUsuario]);

  useEffect(() => {
    if (!servicioId) return;
    const load = async () => {
      setLoading(true);
      try {
        if (id) {
          const design = await getPdfDesignById(servicioId, decodeURIComponent(id));
          if (design) {
            setName(design.name || '');
            setFormId(design.formId || '');
            setLogoUrl(design.logoUrl || '');
            setLogoLeftUrl(design.logoLeftUrl || '');
            setLogoRightUrl(design.logoRightUrl || design.logoUrl || '');
            setPrimaryColor(design.primaryColor || '#FFBF00');
            setAccentColor(design.accentColor || '#FFBF00');
            setHeaderFields(
              Array.isArray(design.headerFields) && design.headerFields.length > 0
                ? design.headerFields.map((h) => ({ label: h.label || '', documentKey: h.documentKey || '' }))
                : [...DEFAULT_HEADER_FIELDS]
            );
            const secs = (design.sections || []).length
              ? design.sections.map((s) => ({
                  title: s.title || '',
                  fieldLabels: Array.isArray(s.fieldLabels) ? s.fieldLabels.join(', ') : (s.fieldLabels || '')
                }))
              : [{ title: '', fieldLabels: '' }];
            setSections(secs);
          }
        } else {
          setName('');
          setFormId('');
          setLogoUrl('');
          setLogoLeftUrl('');
          setLogoRightUrl('');
          setPrimaryColor('#FFBF00');
          setAccentColor('#FFBF00');
          setHeaderFields([...DEFAULT_HEADER_FIELDS]);
          setSections([{ title: '', fieldLabels: '' }]);
        }
      } catch (err) {
        console.error(err);
        setError('Error al cargar el diseño');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [servicioId, id]);

  useEffect(() => {
    if (!servicioId || !formId) {
      setSelectedFormInputs([]);
      return;
    }
    const loadForm = async () => {
      try {
        const formRef = doc(db, 'servicios', servicioId, 'forms', formId);
        const snap = await getDoc(formRef);
        if (!snap.exists()) return;
        const data = snap.data();
        const inputs = data.inputs || {};
        const labels = Object.entries(inputs).map(([k, v]) => (v && v.title) || k);
        setSelectedFormInputs(labels);
      } catch {
        setSelectedFormInputs([]);
      }
    };
    loadForm();
  }, [servicioId, formId]);

  const addSection = () => {
    setSections((prev) => [...prev, { title: '', fieldLabels: '' }]);
  };

  const buildSectionsFromForm = () => {
    if (!selectedFormInputs.length) return;
    setSections(
      selectedFormInputs.map((label) => ({ title: label, fieldLabels: label }))
    );
  };

  const removeSection = (index) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSection = (index, field, value) => {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  };

  const addHeaderField = () => {
    setHeaderFields((prev) => [...prev, { label: '', documentKey: '' }]);
  };
  const removeHeaderField = (index) => {
    setHeaderFields((prev) => prev.filter((_, i) => i !== index));
  };
  const updateHeaderField = (index, field, value) => {
    setHeaderFields((prev) =>
      prev.map((h, i) => (i === index ? { ...h, [field]: value } : h))
    );
  };

  const handleLogoUpload = async (side, file) => {
    if (!servicioId || !file) return;
    setUploadingLogo(side);
    setError('');
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
      const path = `servicios/${servicioId}/pdfDesigns/logos/${side}_${Date.now()}.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      if (side === 'left') setLogoLeftUrl(url);
      else setLogoRightUrl(url);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error al subir el logo');
    } finally {
      setUploadingLogo(null);
    }
  };

  const handleCompanyLogoSelect = (side, company) => {
    const url = company?.logoUrl || '';
    if (side === 'left') setLogoLeftUrl(url);
    else setLogoRightUrl(url);
  };

  const handleClientLogoSelect = (client) => {
    setLogoLeftUrl(client?.logoUrl || '');
  };

  const handleHeaderDragStart = (e, index) => {
    setDraggedHeaderIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };
  const handleHeaderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleHeaderDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = draggedHeaderIndex;
    setDraggedHeaderIndex(null);
    if (dragIndex == null || dragIndex === dropIndex) return;
    setHeaderFields((prev) => {
      const next = [...prev];
      const [removed] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, removed);
      return next;
    });
  };

  const handleSave = async () => {
    setError('');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('El nombre del diseño es obligatorio');
      return;
    }
    if (!servicioId) {
      setError('No se ha cargado el servicio');
      return;
    }
    const payload = {
      name: trimmedName,
      formId: formId || null,
      logoUrl: logoUrl.trim() || null,
      logoLeftUrl: logoLeftUrl.trim() || null,
      logoRightUrl: logoRightUrl.trim() || null,
      // Siempre enviar los colores (usar valor actual o el default)
      primaryColor: primaryColor || '#FFBF00',
      accentColor: accentColor || '#FFBF00',
      // Siempre enviar headerFields (aunque sea vacío, se usarán los defaults al generar PDF)
      headerFields: headerFields.filter((h) => (h.label || '').trim() || (h.documentKey || '').trim()),
      sections: sections
        .filter((s) => (s.title || '').trim() || (s.fieldLabels || '').trim())
        .map((s) => ({
          title: (s.title || '').trim(),
          fieldLabels: (s.fieldLabels || '')
            .split(',')
            .map((l) => l.trim())
            .filter(Boolean)
        }))
    };
    if (id) payload.id = decodeURIComponent(id);
    setSaving(true);
    try {
      await savePdfDesign(servicioId, payload);
      navigate('/pdf-designs');
    } catch (err) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="list">
        <Sidebar />
        <div className={containerClass}>
          <Navbar />
          <div style={{ padding: 24 }}>Cargando...</div>
        </div>
      </div>
    );
  }

  const previewSections = sections.filter((s) => (s.title || '').trim() || (s.fieldLabels || '').trim());

  return (
    <div className="list">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
          <div style={{ flex: '1 1 50%', overflow: 'auto', padding: 24, borderRight: '1px solid #eee' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/pdf-designs')}>
                Volver
              </Button>
              <h2 className="title-registro">{id ? 'Editar diseño PDF' : 'Nuevo diseño PDF'}</h2>
            </div>
            <p style={{ color: '#666', marginBottom: 16 }}>
              Define cómo se agrupan y ordenan los campos en el PDF. Cada sección tiene un título y una lista de etiquetas de campo (separadas por comas) que se mostrarán bajo esa sección.
            </p>
            {error && (
              <div style={{ color: 'crimson', marginBottom: 16 }}>{error}</div>
            )}
            <TextField
            fullWidth
            label="Nombre del diseño"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Parte de trabajo estándar"
            sx={{ marginBottom: 2 }}
          />

          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            <InputLabel>Formulario personalizado (opcional)</InputLabel>
            <Select
              value={formId}
              label="Formulario personalizado (opcional)"
              onChange={(e) => setFormId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">Sin vincular</MenuItem>
              {forms.map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedFormInputs.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ marginBottom: 8, color: '#666' }}>
                Campos del formulario: {selectedFormInputs.join(', ')}
              </p>
              <Button variant="outlined" size="small" onClick={buildSectionsFromForm}>
                Generar secciones desde formulario
              </Button>
            </div>
          )}

          <p style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}>Apariencia del PDF</p>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
            Logo izquierdo y derecho en la cabecera del PDF. Sube una imagen, elige un logo de empresa o (solo izquierdo) un logo de cliente asignado a ti.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16 }}>
              <p style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Logo izquierdo</p>
              <input
                type="file"
                accept="image/*"
                id="logo-left-upload"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload('left', f); e.target.value = ''; }}
              />
              <label htmlFor="logo-left-upload">
                <Button variant="outlined" component="span" size="small" disabled={!!uploadingLogo} startIcon={<ImageIcon />} fullWidth sx={{ mb: 1 }}>
                  {uploadingLogo === 'left' ? 'Subiendo...' : 'Subir imagen'}
                </Button>
              </label>
              {companies.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel shrink>O logo de empresa</InputLabel>
                  <Select
                    value=""
                    label="O logo de empresa"
                    onChange={(e) => handleCompanyLogoSelect('left', companies.find((c) => c.logoUrl === e.target.value) || null)}
                    displayEmpty
                    notched
                  >
                    <MenuItem value="">Ninguno</MenuItem>
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={c.logoUrl}>{c.nombre || c.id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {clientsWithLogos.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel shrink>O logo de cliente</InputLabel>
                  <Select
                    value=""
                    label="O logo de cliente"
                    onChange={(e) => handleClientLogoSelect(clientsWithLogos.find((c) => c.logoUrl === e.target.value) || null)}
                    displayEmpty
                    notched
                  >
                    <MenuItem value="">Ninguno</MenuItem>
                    {clientsWithLogos.map((c) => (
                      <MenuItem key={c.id} value={c.logoUrl}>{c.nombre || c.id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(logoLeftUrl && logoLeftUrl.startsWith('http')) && (
                <div style={{ marginTop: 8 }}>
                  <img src={logoLeftUrl} alt="Logo izq." style={{ maxWidth: '100%', maxHeight: 60, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
            <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16 }}>
              <p style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Logo derecho</p>
              <input
                type="file"
                accept="image/*"
                id="logo-right-upload"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload('right', f); e.target.value = ''; }}
              />
              <label htmlFor="logo-right-upload">
                <Button variant="outlined" component="span" size="small" disabled={!!uploadingLogo} startIcon={<ImageIcon />} fullWidth sx={{ mb: 1 }}>
                  {uploadingLogo === 'right' ? 'Subiendo...' : 'Subir imagen'}
                </Button>
              </label>
              {companies.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel shrink>O logo de empresa</InputLabel>
                  <Select
                    value=""
                    label="O logo de empresa"
                    onChange={(e) => handleCompanyLogoSelect('right', companies.find((c) => c.logoUrl === e.target.value) || null)}
                    displayEmpty
                    notched
                  >
                    <MenuItem value="">Ninguno</MenuItem>
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={c.logoUrl}>{c.nombre || c.id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(logoRightUrl && logoRightUrl.startsWith('http')) && (
                <div style={{ marginTop: 8 }}>
                  <img src={logoRightUrl} alt="Logo der." style={{ maxWidth: '100%', maxHeight: 60, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 14 }}>Color título / encabezados:</label>
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                style={{ width: 40, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, color: '#666' }}>{primaryColor}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 14 }}>Color líneas / pie:</label>
              <input
                type="color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                style={{ width: 40, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, color: '#666' }}>{accentColor}</span>
            </div>
          </div>

          <p style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}>Líneas de cabecera del PDF</p>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
            Etiqueta y clave del documento que se muestran bajo el título (ej. Vigilante, Servicio, Cliente, Fecha de subida). La clave debe coincidir con el campo del documento.
          </p>
          <div style={{ marginBottom: 16 }}>
            {headerFields.map((h, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  marginBottom: 8
                }}
              >
                <TextField
                  size="small"
                  label="Etiqueta"
                  value={h.label}
                  onChange={(e) => updateHeaderField(index, 'label', e.target.value)}
                  placeholder="Ej: Vigilante"
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  size="small"
                  label="Clave del documento"
                  value={h.documentKey}
                  onChange={(e) => updateHeaderField(index, 'documentKey', e.target.value)}
                  placeholder="vigilante, tienda, cliente, fecha..."
                  sx={{ flex: 1, minWidth: 140 }}
                />
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => removeHeaderField(index)}
                  title="Quitar línea"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </div>
            ))}
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addHeaderField}>
              Añadir línea de cabecera
            </Button>
          </div>

          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <strong>Secciones (orden de aparición en el PDF)</strong>
              <Button startIcon={<AddIcon />} onClick={addSection} size="small">
                Añadir sección
              </Button>
            </div>
            {sections.map((sec, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 12,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start'
                }}
              >
                <div style={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Título de la sección"
                    value={sec.title}
                    onChange={(e) => updateSection(index, 'title', e.target.value)}
                    placeholder="Ej: Datos generales"
                    sx={{ marginBottom: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Etiquetas de campos (separadas por comas)"
                    value={sec.fieldLabels}
                    onChange={(e) => updateSection(index, 'fieldLabels', e.target.value)}
                    placeholder="Ej: Fecha, Hora, Vigilante, Servicio"
                    multiline
                    minRows={2}
                  />
                </div>
                <IconButton
                  color="error"
                  onClick={() => removeSection(index)}
                  disabled={sections.length <= 1}
                  title="Quitar sección"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar diseño'}
            </Button>
            <Button onClick={() => navigate('/pdf-designs')}>Cancelar</Button>
          </div>
          </div>

          <div style={{ flex: '1 1 50%', overflow: 'auto', background: '#f0f0f0', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ alignSelf: 'stretch', textAlign: 'center', marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#555' }}>
              Vista previa del PDF
            </div>
            <div
              className="pdf-preview-paper"
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: 595,
                minHeight: 842,
                background: '#fff',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                padding: 28,
                paddingBottom: 36,
                boxSizing: 'border-box',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: 12
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 }}>
                <div style={{ flex: 1, minHeight: 70, display: 'flex', alignItems: 'center' }}>
                  {(logoLeftUrl && logoLeftUrl.startsWith('http')) ? (
                    <img src={logoLeftUrl} alt="Logo izq." style={{ maxWidth: 170, maxHeight: 70, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div style={{ width: 120, height: 70, border: '1px dashed #ccc', borderRadius: 4, background: '#fafafa', color: '#999', fontSize: 10, lineHeight: '70px', textAlign: 'center' }}>Logo izquierdo</div>
                  )}
                </div>
                <div style={{ flex: 1, minHeight: 70, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  {(logoRightUrl && logoRightUrl.startsWith('http')) || (logoUrl && logoUrl.startsWith('http')) ? (
                    <img src={logoRightUrl || logoUrl} alt="Logo der." style={{ maxWidth: 170, maxHeight: 70, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div style={{ width: 120, height: 70, border: '1px dashed #ccc', borderRadius: 4, background: '#fafafa', color: '#999', fontSize: 10, lineHeight: '70px', textAlign: 'center' }}>Logo derecho</div>
                  )}
                </div>
              </div>
              <h1 style={{ textAlign: 'center', fontSize: 24, color: primaryColor, margin: '0 0 20px 0', fontWeight: 'bold' }}>
                {name || 'Nombre del documento'}
              </h1>
              <div style={{ marginBottom: 24, fontSize: 12, color: '#000' }}>
                <p style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Arrastra las líneas para cambiar el orden</p>
                {headerFields.some((h) => (h.label || '').trim() || (h.documentKey || '').trim()) ? (
                  headerFields.map((h, idx) => {
                    const hasContent = (h.label || '').trim() || (h.documentKey || '').trim();
                    return (
                      <div
                        key={idx}
                        draggable
                        onDragStart={(e) => { e.stopPropagation(); handleHeaderDragStart(e, idx); }}
                        onDragOver={(e) => { e.preventDefault(); handleHeaderDragOver(e); }}
                        onDrop={(e) => { e.preventDefault(); handleHeaderDrop(e, idx); }}
                        style={{
                          marginBottom: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          cursor: 'grab',
                          padding: '4px 6px',
                          borderRadius: 4,
                          background: draggedHeaderIndex === idx ? '#e3f2fd' : 'transparent'
                        }}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 18, color: '#999' }} />
                        <span>
                          {hasContent ? (
                            <><strong>{(h.label || h.documentKey || 'Campo').trim()}:</strong>{' '}
                              {h.documentKey === 'vigilante' ? 'usuario@ejemplo.com' : h.documentKey === 'tienda' ? '123 - Tienda ejemplo' : h.documentKey === 'cliente' ? 'Cliente ejemplo' : h.documentKey === 'fecha' ? '30/1/2026' : 'valor'}</>
                          ) : (
                            <span style={{ color: '#999' }}>— línea vacía —</span>
                          )}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <>
                    <div style={{ marginBottom: 6 }}><strong>Vigilante:</strong> usuario@ejemplo.com</div>
                    <div style={{ marginBottom: 6 }}><strong>Servicio:</strong> 123 - Tienda ejemplo</div>
                    <div style={{ marginBottom: 6 }}><strong>Cliente:</strong> Cliente ejemplo</div>
                    <div style={{ marginBottom: 6 }}><strong>Fecha de subida:</strong> 30/1/2026</div>
                  </>
                )}
              </div>
              {previewSections.length > 0 ? (
                previewSections.map((sec, idx) => {
                  const labels = (sec.fieldLabels || '').split(',').map((l) => l.trim()).filter(Boolean);
                  return (
                    <div key={idx} style={{ marginBottom: 20 }}>
                      <div style={{ fontWeight: 'bold', fontSize: 13, color: '#000', marginBottom: 4 }}>{sec.title || 'Sin título'}</div>
                      <div style={{ height: 2, background: accentColor, marginBottom: 10 }} />
                      {labels.length > 0 ? (
                        labels.map((label, i) => (
                          <div key={i} style={{ marginBottom: 6, fontSize: 12 }}>{label}: <span style={{ color: '#555' }}>valor de ejemplo</span></div>
                        ))
                      ) : (
                        <div style={{ color: '#999', fontSize: 11 }}>— campos (separados por comas) —</div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{ color: '#999', fontSize: 12 }}>Añade secciones para ver la vista previa.</div>
              )}
              <div style={{ marginTop: 24, padding: '12px 0', borderTop: `2px solid ${accentColor}`, fontSize: 11, color: '#666' }}>
                <strong>Páginas siguientes en el PDF:</strong> Fotos adjuntas · Firmas del documento
              </div>
              <div style={{ position: 'absolute', bottom: 0, left: 28, right: 28, height: 10, background: accentColor }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfDesignEditor;
